import * as React from 'react';
import { ApolloConsumer } from 'react-apollo';
import { defaultMemoize } from 'reselect';
import * as shallowEqual from 'shallowequal';
import { DataCatalogApolloClient } from '../apolloClientGenerator';
import { PaginatedRowStore } from '../dataGrid/PaginatedRowStore';
import { GraphQLError } from './GraphQLError';
import {
  GraphQLSliceableParams,
  makeRowFetcher,
  PaginationQueryVariables, PartialQueryVariables
} from './GraphQLSliceable';
import { ItemWithId } from './ItemWithId';

interface QueryVariablesParams<TQueryVariables extends PaginationQueryVariables> {
  readonly partialQueryVariables: PartialQueryVariables<TQueryVariables>;
}

export interface PaginatedQueryWrapperProps<
  TQueryVariables extends PaginationQueryVariables,
  TQueryResults,
  TQueryResultListNode extends ItemWithId,
  TResultItem extends ItemWithId,
  > extends GraphQLSliceableParams<TQueryResults, TQueryResultListNode, TResultItem>,
  QueryVariablesParams<TQueryVariables> {

  readonly hackyUpdateNumber?: number;
  readonly children: (props: {
    /** The rowStore can be used to fetch slices of items in the paginated query. */
    rowStore: PaginatedRowStore<TResultItem, GraphQLError>
  }) => JSX.Element;
}

/**
 * The PaginatedQueryWrapper is a provider component that gives its children access to a paginated GraphQL query.
 */
export class PaginatedQueryWrapper<
  TQueryVariables extends PaginationQueryVariables,
  TQueryResults,
  TQueryResultListNode extends ItemWithId,
  TResultItem extends ItemWithId,
  > extends React.Component<PaginatedQueryWrapperProps<TQueryVariables, TQueryResults, TQueryResultListNode, TResultItem>> {

  /*
   * We use a custom memoizer for the row store that extracts the partial query variables from the props
   * and checks shallow equality on them separately, so that if the client passes in a new object
   * containing the same query variables, we don't obliterate the previous row store.
   */
  // tslint:disable-next-line:typedef
  private memoizedCreateRowStore = defaultMemoize(createRowStore, (a, b) => shallowEqual(a, b));

  public render() {
    return (
      <ApolloConsumer>
        {(client) => {
          const { children, partialQueryVariables, ...rowFetcherParams } = this.props;
          const rowStore = this.memoizedCreateRowStore(rowFetcherParams, partialQueryVariables, client);
          return children({ rowStore });
        }}
      </ApolloConsumer>
    );
  }
}


function createRowStore<
  TQueryVariables extends PaginationQueryVariables,
  TQueryResults,
  TQueryResultListNode extends ItemWithId,
  TResultItem extends ItemWithId
>(
  sliceableFactoryParams: GraphQLSliceableParams<TQueryResults, TQueryResultListNode, TResultItem>,
  partialQueryVariables: PartialQueryVariables<TQueryVariables>,
  client: DataCatalogApolloClient,
) {
  const queryRows = makeRowFetcher(sliceableFactoryParams, partialQueryVariables, client);
  return new PaginatedRowStore(queryRows);
}
